
// 错误处理工具

// API 错误类型
export interface ApiError extends Error {
  status?: number;
  statusText?: string;
  data?: unknown;
  code?: string;
}

// 错误类型常量
export const ErrorTypes = {
  API: 'api',
  VALIDATION: 'validation',
  NETWORK: 'network',
  AUTHENTICATION: 'authentication',
  AUTHORIZATION: 'authorization',
  RESOURCE: 'resource',
  SERVER: 'server',
  UNKNOWN: 'unknown'
} as const;

export type ErrorType = typeof ErrorTypes[keyof typeof ErrorTypes];

// 错误信息接口
export interface ErrorInfo {
  type: ErrorType;
  message: string;
  details?: unknown;
  status?: number;
  code?: string;
  action?: string;
}

// 错误代码映射
const ERROR_CODES: Record<string, string> = {
  'USER_NOT_FOUND': '用户不存在',
  'INVALID_PASSWORD': '密码错误',
  'EMAIL_ALREADY_EXISTS': '邮箱已被注册',
  'PHONE_ALREADY_EXISTS': '手机号已被注册',
  'ORDER_NOT_FOUND': '订单不存在',
  'CUSTOMER_NOT_FOUND': '客户不存在',
  'INSUFFICIENT_PERMISSIONS': '权限不足',
  'FILE_TOO_LARGE': '文件大小超过限制',
  'INVALID_FILE_TYPE': '文件类型不支持',
  'DATABASE_ERROR': '数据库操作失败',
  'NETWORK_ERROR': '网络连接失败',
  'SERVER_ERROR': '服务器内部错误'
};

// 处理 API 错误
export function handleApiError(error: unknown): ErrorInfo {
  // 归一化：API 错误 / 原生 Error / 未知对象统一为可安全读取的结构
  const source = (error ?? {}) as {
    message?: string;
    status?: number;
    code?: string;
    data?: { code?: string; errors?: unknown } | null;
    error?: { code?: string } | null;
    errors?: unknown;
  };
  const { status, message, data, error: nestedError, errors } = source;
  const errorCode = source.code || data?.code || nestedError?.code;
  
  if (status) {
    switch (status) {
      case 400: {
        // 服务端写了整句话时，那句话本身就是下一步（闸门类拒绝如「如需改期请取消这张单后重新提交」）。
        // 再补一句通用「请检查输入参数后重试」会把这种拒绝说成用户填错了表单，反而稀释掉准确文案；
        // 只有服务端没给 message（网关/HTML 错误页）落到兜底串时，那句建议才有意义。
        const serverMessage = errorCode
          ? ERROR_CODES[errorCode] || message
          : message;
        return {
          type: ErrorTypes.VALIDATION,
          message: serverMessage || '请求参数错误',
          status: status,
          code: errorCode,
          details: data?.errors || errors,
          ...(serverMessage ? {} : { action: '请检查输入参数后重试' }),
        };
      }
      case 401:
        return {
          type: ErrorTypes.AUTHENTICATION,
          message: errorCode ? ERROR_CODES[errorCode] || '登录已过期，请重新登录' : '登录已过期，请重新登录',
          status: status,
          code: errorCode,
          action: '请重新登录'
        };
      case 403:
        return {
          type: ErrorTypes.AUTHORIZATION,
          message: errorCode ? ERROR_CODES[errorCode] || '没有权限执行此操作' : '没有权限执行此操作',
          status: status,
          code: errorCode,
          action: '请联系管理员获取权限'
        };
      case 404:
        return {
          type: ErrorTypes.RESOURCE,
          message: errorCode ? ERROR_CODES[errorCode] || '请求的资源不存在' : '请求的资源不存在',
          status: status,
          code: errorCode,
          action: '请检查请求路径是否正确'
        };
      case 409:
        return {
          type: ErrorTypes.VALIDATION,
          message: errorCode ? ERROR_CODES[errorCode] || message || '操作冲突，请刷新后重试' : message || '操作冲突，请刷新后重试',
          status: status,
          code: errorCode,
          action: '请刷新后确认最新数据，再重新提交'
        };
      case 413:
        return {
          type: ErrorTypes.VALIDATION,
          message: errorCode ? ERROR_CODES[errorCode] || '请求实体过大' : '请求实体过大',
          status: status,
          code: errorCode,
          action: '请减小文件大小或请求数据量'
        };
      case 415:
        return {
          type: ErrorTypes.VALIDATION,
          message: errorCode ? ERROR_CODES[errorCode] || '不支持的媒体类型' : '不支持的媒体类型',
          status: status,
          code: errorCode,
          action: '请使用正确的文件类型'
        };
      case 500:
        return {
          type: ErrorTypes.SERVER,
          message: errorCode ? ERROR_CODES[errorCode] || '服务器内部错误，请稍后重试' : '服务器内部错误，请稍后重试',
          status: status,
          code: errorCode,
          action: '请稍后重试或联系管理员'
        };
      case 502:
        return {
          type: ErrorTypes.SERVER,
          message: '网关错误',
          status: status,
          action: '请稍后重试'
        };
      case 503:
        return {
          type: ErrorTypes.SERVER,
          message: '服务不可用',
          status: status,
          action: '请稍后重试'
        };
      case 504:
        return {
          type: ErrorTypes.SERVER,
          message: '网关超时',
          status: status,
          action: '请稍后重试'
        };
      default:
        return {
          type: ErrorTypes.API,
          message: message || `请求失败 (${status})`,
          status: status,
          code: errorCode
        };
    }
  } else if (errorCode === 'NETWORK_ERROR' || message?.includes('Network')) {
    return {
      type: ErrorTypes.NETWORK,
      // `NETWORK_ERROR` 由 api/client.ts 在出口打上，那句话已经是中文；这里不再换一遍措辞，
      // 免得同一次断网在两个地方得到两句略有差别的话。消息串里带 'Network' 的是没经过
      // 那条出口的原生错误（浏览器直接抛的 `Failed to fetch` 这类），才需要现编中文。
      message: errorCode === 'NETWORK_ERROR' && message ? message : '网络连接失败，请检查网络设置',
      code: 'NETWORK_ERROR',
      action: '请检查网络连接后重试'
    };
  } else if (message?.includes('Timeout')) {
    return {
      type: ErrorTypes.NETWORK,
      message: '请求超时，请检查网络连接',
      code: 'TIMEOUT_ERROR',
      action: '请检查网络连接后重试'
    };
  } else {
    return {
      type: ErrorTypes.UNKNOWN,
      message: message || '未知错误',
      code: errorCode
    };
  }
}

/**
 * 「这次写入和已有数据撞了」的统一判定。服务端有两道闸门、两种形状：
 * 数据库档期部分唯一索引回 409，服务层的查重预检回 2xx + success:false
 * （由 api/client.ts 的 unwrapResponse 抛成带 isBusinessError 的异常）。
 * 两者对用户是同一件事——提交重复了、或手上这份快照已经过期，处理必须一致，
 * 否则同一个「已有一张单」在一半路径上有提示、另一半静默什么都不做。
 */
export function isWriteConflict(error: unknown, info: ErrorInfo): boolean {
  return info.status === 409
    || (error as { isBusinessError?: boolean } | null)?.isBusinessError === true;
}

/** `POST /orders` 命中疑似重复订单时后端带回的那张候选单（判重靠它，不靠客户端自己拼） */
export interface SlotDuplicateHintItem {
  orderId: string;
  orderNumber: string;
  customerName: string | null;
  orderDate: string | null;
  orderAmount: number;
  paidAmount: number;
  orderStatus: string;
  approvalStatus: string;
  /**
   * 候选单占的钟点，以及「它和本次提交的时间段叠不叠」——都由服务端算（order-slot-time 是唯一一份实现）。
   * 四端只读这两项，不许再各自拿 HH:mm 字符串比一遍。
   */
  isFullDay?: boolean;
  orderTimeStart?: string | null;
  orderTimeEnd?: string | null;
  timeLabel?: string;
  timeOverlap?: boolean;
  submittedBy: string;
  submittedAt: string | null;
  submittedByMe: boolean;
}

/**
 * `GET /orders/slot-preview` 的返回：表单「填完预约日期」时的即时预告。
 *
 * 与提交闸门共用同一份判据，但**只读、不阻断**——`hasConflict` 为真也只说明
 * 「同日已经有单了，请核对」，真正拦下提交的是随后那张 `needConfirm` 确认框。
 * `checked:false` 表示这一条压根没检（支出分类免检），不是「检了且没有冲突」。
 */
export interface SlotPreviewResult {
  checked: boolean;
  hasConflict: boolean;
  conflictCount: number;
  overlapCount: number;
  message: string;
  conflicts: SlotDuplicateHintItem[];
}

/**
 * 「这张单可能是你（或对方）已经提交过的那张」——服务端只提示、由人裁定的那条通道。
 *
 * 它带 `success: false` 却**不是失败**：HTTP 201、由 `unwrapResponse` 抛成异常，
 * 形状与真错误混在一起。所以调用方必须**先**问这一条、再走 `showErrorNotification`
 * 与 `isWriteConflict`——按失败处理会把一句「请核对」演成一次刷新，用户对着重新弹出的
 * 空白表单完全不知道该核对什么。
 */
export function getSlotDuplicateHint(
  error: unknown,
): { message: string; duplicates: SlotDuplicateHintItem[] } | null {
  const e = error as { needConfirm?: unknown; duplicates?: unknown; message?: unknown } | null;
  if (!e || e.needConfirm !== 'slot_duplicate' || !Array.isArray(e.duplicates) || e.duplicates.length === 0) {
    return null;
  }
  return {
    message: typeof e.message === 'string' ? e.message : '',
    duplicates: e.duplicates as SlotDuplicateHintItem[],
  };
}

// 处理表单验证错误
export function handleValidationError(errors: unknown): string[] {
  const errorMessages: string[] = [];

  if (!errors || typeof errors !== 'object') return errorMessages;

  function collectErrors(obj: Record<string, unknown>, prefix: string = '') {
    for (const key in obj) {
      const value = obj[key];
      if (typeof value === 'object' && value !== null) {
        collectErrors(value as Record<string, unknown>, `${prefix}${key}.`);
      } else if ((value as { message?: unknown })?.message) {
        errorMessages.push(String((value as { message?: unknown }).message));
      }
    }
  }

  collectErrors(errors as Record<string, unknown>);
  return errorMessages;
}

// 显示错误通知
export function showErrorNotification(errorInfo: ErrorInfo | string) {
  // 处理字符串类型的错误信息
  const info = typeof errorInfo === 'string' 
    ? { type: ErrorTypes.UNKNOWN, message: errorInfo } 
    : errorInfo;
  
  // 这里可以集成第三方通知库，如 react-toastify
  console.error(`[${info.type}] ${info.message}`, info);
  
  // 根据错误类型选择不同的样式
  const getErrorStyle = (type: ErrorType) => {
    switch (type) {
      case ErrorTypes.AUTHENTICATION:
      case ErrorTypes.AUTHORIZATION:
        return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', icon: 'ri-shield-cross-line' };
      case ErrorTypes.NETWORK:
        return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', icon: 'ri-wifi-off-line' };
      case ErrorTypes.SERVER:
        return { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600', icon: 'ri-server-line' };
      case ErrorTypes.RESOURCE:
        return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', icon: 'ri-file-warning-line' };
      default:
        return { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-600', icon: 'ri-error-warning-line' };
    }
  };
  
  const style = getErrorStyle(info.type);
  
  // 简单的实现：创建一个临时的错误通知元素
  const notification = document.createElement('div');
  notification.className = `fixed top-4 right-4 ${style.bg} ${style.border} rounded-lg p-4 shadow-lg z-50 flex flex-col items-start gap-2 max-w-md`;
  
  // 构建通知内容
  let content = `
    <div class="flex items-center gap-2 w-full">
      <div class="w-4 h-4 flex items-center justify-center ${style.text}">
        <i class="${style.icon}" />
      </div>
      <span class="text-sm ${style.text} flex-1">${info.message}</span>
    </div>
  `;
  
  // 添加操作建议
  if (info.action) {
    content += `
      <div class="w-full pt-2 border-t border-gray-100">
        <span class="text-xs ${style.text} opacity-80">${info.action}</span>
      </div>
    `;
  }
  
  notification.innerHTML = content;
  
  document.body.appendChild(notification);
  
  // 5秒后自动移除
  setTimeout(() => {
    notification.style.opacity = '0';
    notification.style.transition = 'opacity 0.3s ease';
    setTimeout(() => {
      try {
        document.body.removeChild(notification);
      } catch (e) {
        // 忽略已经被移除的情况
      }
    }, 300);
  }, 5000);
}
